<template>
  <b-modal
    modal-class="component-modal-product"
    id="productItemModal"
    ref="productItemModal"
    size="xl"
    :title="modalTitle"
    no-close-on-backdrop
    @close="close"
  >
    <b-form id="productTable" @submit.prevent="saveProduct">
      <!-- productCode -->
      <b-form-group label-cols="3" label="รหัสสินค้า:" label-align="right">
        <b-form-input
          class="required"
          id="productCode"
          v-model="productItem.productCode"
          :class="{ 'is-invalid': $v.productItem.productCode.$error }"
          maxlength="20"
          :disabled="productItem.productStatus === 'LOCKED'"
          placeholder="กรอกรหัสสินค้า (รหัส SKU)"
          @keypress="preventInvalidKey"
          @input="formatProductCode"
        ></b-form-input>
        <template v-slot:description> </template>
      </b-form-group>

      <!-- productCategory -->
      <b-form-group label-cols="3" label="หมวดหมู่:" label-align="right">
        <multiselect
          class="col p-0 required"
          :class="{ 'is-invalid': $v.productItem.productCategory.$error }"
          v-model="productItem.productCategory"
          :options="categoryOptions"
          :multiple="false"
          placeholder="เลือกหมวดหมู่สินค้า"
          track-by="id"
          :custom-label="customLabel"
          selectLabel="เลือกหมวดหมู่สินค้า"
          selectedLabel="เลือกแล้ว"
          deselectLabel="คลิกเพื่อลบออก"
          openDirection="below"
          :disabled="productItem.productStatus === 'LOCKED'"
        >
          <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
        </multiselect>
      </b-form-group>

      <!-- productNameMain -->
      <b-form-group label-cols="3" label="ชื่อสินค้า:" label-align="right">
        <b-form-input
          class="required"
          id="productNameMain"
          maxlength="100"
          v-model="productItem.productNameMain"
          placeholder="กรอกชื่อสินค้า"
          :class="{ 'is-invalid': $v.productItem.productNameMain.$error }"
          :disabled="productItem.productStatus === 'LOCKED'"
        ></b-form-input>
        <template v-slot:description> </template>
      </b-form-group>

      <!-- productNameAdditional -->
      <b-form-group
        label-cols="3"
        label="ชื่อสินค้าเพิ่มเติม:"
        label-align="right"
      >
        <b-form-input
          id="productNameAdditional"
          maxlength="50"
          v-model="productItem.productNameAdditional"
          placeholder="กรอกชื่อสินค้าเพิ่มเติม หรือรายละเอียดสินค้า (ถ้ามี)"
          :disabled="productItem.productStatus === 'LOCKED'"
        ></b-form-input>
        <template v-slot:description> </template>
      </b-form-group>

      <!-- productUnit -->
      <b-form-group label-cols="3" label="หน่วยย่อยสินค้า:" label-align="right">
        <b-row>
          <multiselect
            class="col p-0 required ml-3 mr-2"
            :class="{ 'is-invalid': $v.productItem.productUnit.$error }"
            v-model="productItem.productUnit"
            :options="unitOptions"
            :multiple="false"
            placeholder="เลือกหน่วยที่ย่อยที่สุดของสินค้า"
            track-by="id"
            :custom-label="customLabel"
            selectLabel="เลือกหน่วยสินค้า"
            selectedLabel="เลือกแล้ว"
            deselectLabel="คลิกเพื่อลบออก"
            openDirection="below"
            :disabled="productItem.productStatus === 'LOCKED'"
          >
            <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
          </multiselect>
          <b-button
            class="col-4 col-lg-3 mr-3"
            variant="outline-primary"
            :disabled="!productItem.productUnit"
            @click="toggleSubUnitTable"
            >หน่วยเทียบ</b-button
          >
        </b-row>
      </b-form-group>

      <!-- pruductSubUnit -->
      <b-row v-if="showSubUnitTable">
        <b-col cols="3"></b-col>

        <b-col class="layout-table">
          <div class="sub-unit-table">
            <b-table
              id="ProductSubUnitTable"
              ref="ProductSubUnitTable"
              hover
              small
              sort-icon-left
              head-variant="light"
              :items="productItem.productConversionUnitItems"
              :fields="fields"
            >
              <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
              <template v-slot:cell(productSubUnit)="row">
                <multiselect
                  class="col p-0 required"
                  v-model="row.item.productSubUnit"
                  :options="getSubUnitOptions(row.index)"
                  :multiple="false"
                  placeholder="เลือกหน่วยสินค้า"
                  track-by="id"
                  :custom-label="customLabel"
                  selectLabel="เลือกหน่วยสินค้า"
                  selectedLabel="เลือกแล้ว"
                  deselectLabel="คลิกเพื่อลบออก"
                  openDirection="below"
                  :class="{
                    'is-invalid':
                      $v.productItem.productConversionUnitItems.$each[
                        row.index
                      ] &&
                      $v.productItem.productConversionUnitItems.$each[row.index]
                        .productSubUnit.$error,
                  }"
                >
                  <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
                </multiselect>
              </template>

              <template v-slot:cell(productConversionUnit)="row">
                <b-input-group class="col">
                  <b-form-input
                    v-model="row.item.productConversionUnit"
                    class="required"
                    placeholder="กรอกจำนวน"
                    :class="{
                      'is-invalid':
                        $v.productItem.productConversionUnitItems.$each[
                          row.index
                        ] &&
                        $v.productItem.productConversionUnitItems.$each[
                          row.index
                        ].productConversionUnit.$error,
                    }"
                    inputmode="numeric"
                    @keydown="allowOnlyNumber($event)"
                  ></b-form-input>
                  <template v-slot:append>
                    <b-input-group-text class="append-text">{{
                      productItem.productUnit?.name || ""
                    }}</b-input-group-text>
                  </template>
                </b-input-group>
              </template>

              <template v-slot:cell(buttons)="row">
                <b-button
                  variant="outline-danger"
                  @click="deleteProductSubUnitItem(row.item, row.index)"
                >
                  <i class="fas fa-trash-alt"></i>
                </b-button>
              </template>

              <template v-slot:bottom-row>
                <td colspan="4" class="bottom-row-cell">
                  <b-button
                    type="button"
                    variant="primary"
                    @click="addProductSubUnitItem"
                    class="bottom-add-row"
                  >
                    <i class="fas fa-plus"></i> เพิ่มหน่วย
                  </b-button>
                </td>
              </template>
            </b-table>
          </div>
        </b-col>
      </b-row>

      <!-- productPurchaseUnit -->
      <b-form-group label-cols="3" label="หน่วยซื้อ:" label-align="right">
        <multiselect
          class="col p-0 required"
          :class="{ 'is-invalid': $v.productItem.productPurchaseUnit.$error }"
          v-model="productItem.productPurchaseUnit"
          :options="unitOptions"
          :multiple="false"
          placeholder="เลือกหน่วยสำหรับซื้อ"
          track-by="id"
          :custom-label="customLabel"
          selectLabel="เลือกหน่วยสำหรับซื้อ"
          selectedLabel="เลือกแล้ว"
          :allow-empty="false"
          :clear-on-select="false"
          deselectLabel=""
          openDirection="below"
          :disabled="
            !productItem.productUnit || productItem.productStatus === 'LOCKED'
          "
        >
          <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
        </multiselect>
      </b-form-group>

      <!-- productSalesUnit -->
      <b-form-group label-cols="3" label="หน่วยขาย:" label-align="right">
        <multiselect
          class="col p-0 required"
          :class="{ 'is-invalid': $v.productItem.productSalesUnit.$error }"
          v-model="productItem.productSalesUnit"
          :options="unitOptions"
          :multiple="false"
          placeholder="เลือกหน่วยสำหรับขาย"
          track-by="id"
          :custom-label="customLabel"
          selectLabel="เลือกหน่วยสำหรับขาย"
          selectedLabel="เลือกแล้ว"
          :allow-empty="false"
          :clear-on-select="false"
          deselectLabel=""
          openDirection="below"
          :disabled="
            !productItem.productUnit || productItem.productStatus === 'LOCKED'
          "
        >
          <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
        </multiselect>
      </b-form-group>
    </b-form>

    <template v-slot:modal-footer>
      <b-button
        v-if="!isNewProduct && productItem.productStatus !== 'LOCKED'"
        variant="danger"
        @click="deleteProduct(productItem)"
        >ลบรายการ</b-button
      >
      <b-button type="submit" form="productTable" variant="primary">
        {{ isNewProduct ? "สร้างรายการ" : "แก้ไขรายการ" }}
      </b-button>
    </template>
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </b-modal>
</template>

<script>
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import "@/theme/modal/ProductAndStock/Product.scss";
import {
  integer,
  maxLength,
  minValue,
  required,
} from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";
import {
  alphaNumUpperSymbol,
  preventInvalidKey,
  formatCodeInput,
} from "@/utils/inputCodeValidator";

export default {
  name: "ProductCreateModal",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      modalTitle: "รายการสินค้า",
      isNewProduct: true,
      showSubUnitTable: false,
      unitList: [],
      categoryList: [],
      productItem: {
        productId: "",
        productCode: "",
        productCategory: "",
        productNameMain: "",
        productNameAdditional: "",
        productUnit: "",
        productConversionUnitItems: [],
        productPurchaseUnit: "",
        productSalesUnit: "",
        productStatus: "",
      },
      fields: [
        {
          key: "index",
          label: "",
          class: "text-center",
          thStyle: { width: "5%" },
          tdStyle: { width: "5%" },
        },
        {
          key: "productSubUnit",
          label: "ชื่อหน่วยสินค้า",
          thStyle: { width: "43%" },
          tdStyle: { width: "43%" },
        },
        {
          key: "productConversionUnit",
          label: "จำนวนเมื่อเทียบกับหน่วยย่อย",
          thStyle: { width: "42%" },
          tdStyle: { width: "42%" },
        },
        {
          key: "buttons",
          label: "",
          class: "text-center",
          thStyle: { width: "10%" },
          tdStyle: { width: "10%" },
        },
      ],
    };
  },
  validations: {
    productItem: {
      productCode: {
        required,
        alphaNumUpperSymbol,
        maxLength: maxLength(20),
      },
      productCategory: {
        required,
      },
      productNameMain: {
        required,
        maxLength: maxLength(100),
      },
      productNameAdditional: {
        maxLength: maxLength(50),
      },
      productUnit: {
        required,
      },
      productPurchaseUnit: {
        // required,
        required(value) {
          if (!this.productItem.productUnit) return true;
          return required(value);
        },
      },
      productSalesUnit: {
        // required,
        required(value) {
          if (!this.productItem.productUnit) return true;
          return required(value);
        },
      },
      productConversionUnitItems: {
        $each: {
          productSubUnit: {
            required(value) {
              if (!this.showSubUnitTable) return true;
              return !!(value && value.id);
            },
          },
          productConversionUnit: {
            required(value) {
              if (!this.showSubUnitTable) return true;
              return !!value && Number(value) !== 0;
            },
          },
        },
      },
    },
  },
  computed: {
    unitOptions() {
      return this.unitList.map(unit => ({
        id: unit.unitId,
        name: unit.unitNameTh,
      }));
    },
    categoryOptions() {
      return this.categoryList.map(category => ({
        id: category.categoryId,
        name: category.categoryNameTh,
      }));
    },
    ...mapGetters({
      getProductList: "moduleProductInventory/getProductList",
    }),
  },
  methods: {
    ...mapActions({
      fetchProductList: "moduleProductInventory/fetchProductList",
    }),
    async show(data) {
      const { product, unitList, categoryList } = data;

      this.unitList = unitList;
      this.categoryList = categoryList;
      if (product) {
        this.isNewProduct = false;
        this.mapProductData(product);
      } else {
        this.isNewProduct = true;
        this.setDefaultValue();
      }
      this.$bvModal.show("productItemModal");
    },
    close() {
      this.$v.$reset();
      this.setDefaultValue();
      this.isNewProduct = true;
      this.$bvModal.hide("productItemModal");
    },
    preventInvalidKey,
    formatProductCode(value) {
      this.productItem.productCode = formatCodeInput(value);
    },
    reloadProductList() {
      this.fetchProductList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    setDefaultValue() {
      this.productItem = Object.assign(
        {},
        {
          productCode: "",
          productCategory: "",
          productNameMain: "",
          productNameAdditional: "",
          productUnit: "",
          productConversionUnitItems: [],
          productPurchaseUnit: "",
          productSalesUnit: "",
          productStatus: "",
        }
      );
    },
    saveProduct() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      const adjustItems = this.productItem.productConversionUnitItems.map(
        item => ({
          ...(item.id && { id: item.id }),
          uomId: item.productSubUnit.id,
          qtyPerUnitOfMeasure: Number(item.productConversionUnit),
        })
      );

      const adjustProductItem = {
        no: this.productItem.productCode,
        categoryId: this.productItem.productCategory.id,
        description: this.productItem.productNameMain,
        description2: this.productItem.productNameAdditional,
        baseUomId: this.productItem.productUnit.id,
        saleUomId: this.productItem.productSalesUnit.id,
        purchaseUomId: this.productItem.productPurchaseUnit.id,
        productUomConversions: adjustItems,
      };

      if (this.isNewProduct) {
        this.$store
          .dispatch("moduleProductInventory/createProductItem", {
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            adjustProductItem,
          })
          .then(res => {
            if (res.status == 201) {
              this.reloadProductList();
              this.$parent.showDialogToast(
                "success",
                "เพิ่มรายการหน่วยสินค้าสำเร็จ"
              );
            }
          })
          .catch(err => {
            const errorMessage = err.response?.data?.errorMessage;

            const errorMsg =
              typeof errorMessage === "string"
                ? errorMessage
                : "เพิ่มรายการหน่วยสินค้าไม่สำเร็จ กรุณาลองใหม่";

            this.$parent.showDialogToast("danger", errorMsg);
          })
          .finally(() => {
            this.close();
            this.isLoading = false;
          });
      } else {
        this.$store
          .dispatch("moduleProductInventory/updateProductItem", {
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            adjustProductItem,
            id: this.productItem.productId,
          })
          .then(res => {
            if (res.status == 200) {
              this.reloadProductList();
              this.$parent.showDialogToast(
                "success",
                "แก้ไขรายการสินค้าสำเร็จ"
              );
            }
          })
          .catch(err => {
            const errorMessage = err.response?.data?.errorMessage;

            const errorMsg =
              typeof errorMessage === "string"
                ? errorMessage
                : "แก้ไขรายการสินค้าไม่สำเร็จ กรุณาลองใหม่";

            this.$parent.showDialogToast("danger", errorMsg);
          })
          .finally(() => {
            this.close();
            this.isLoading = false;
          });
      }
    },
    deleteProduct(eventData) {
      this.$refs.Dialog.showAlertConfirm(
        "ลบสินค้านี้ ?",
        "คุณต้องการลบสินค้านี้ใช่หรือไม่"
      ).then(result => {
        if (!result.value) return;
        this.isLoading = true;
        this.$store
          .dispatch("moduleProductInventory/deleteProductItem", {
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            id: this.productItem.productId,
          })
          .then(res => {
            if (res.status == 200) {
              this.reloadProductList();
              this.$parent.showDialogToast(
                "success",
                "ลบรายการหน่วยสินค้าสำเร็จ"
              );
            }
          })
          .catch(err => {
            const errorMessage = err.response?.data?.errorMessage;

            const errorMsg =
              typeof errorMessage === "string"
                ? errorMessage
                : "ลบรายการหน่วยสินค้าไม่สำเร็จ กรุณาลองใหม่";

            this.$parent.showDialogToast("danger", errorMsg);
          })
          .finally(() => {
            this.close();
            this.isLoading = false;
          });
      });
    },
    getSubUnitOptions(currentIndex) {
      let options = this.unitOptions;

      if (this.productItem.productUnit) {
        options = options.filter(
          unit => unit.id !== this.productItem.productUnit.id
        );
      }

      // Filter out units already selected in other rows
      const selectedUnitIds = this.productItem.productConversionUnitItems
        .map((item, index) => {
          // Skip the current row
          if (index === currentIndex) return null;
          // Return the selected unit id if it exists
          return item.productSubUnit?.id || null;
        })
        .filter(id => id !== null);

      return options.filter(unit => !selectedUnitIds.includes(unit.id));
    },
    customLabel(item) {
      return item?.name || "";
    },
    createEmptyItem() {
      return {
        productSubUnit: "",
        productConversionUnit: "",
      };
    },
    addProductSubUnitItem() {
      this.productItem.productConversionUnitItems.push(this.createEmptyItem());
    },
    deleteProductSubUnitItem(eventData, index) {
      this.productItem.productConversionUnitItems.splice(index, 1);
      if (this.productItem.productConversionUnitItems.length === 0) {
        this.showSubUnitTable = false;
      }
    },
    toggleSubUnitTable() {
      const willShow = !this.showSubUnitTable;

      if (willShow) {
        this.productItem.productConversionUnitItems = [this.createEmptyItem()];
        if (this.$v.productItem.productConversionUnitItems) {
          this.$v.productItem.productConversionUnitItems.$reset();
        }
      } else {
        this.productItem.productConversionUnitItems = [];
      }

      this.showSubUnitTable = willShow;
    },
    mapProductData(data) {
      const unit =
        this.unitOptions.find(u => u.id === data.productUnit.id) || null;

      const category =
        this.categoryOptions.find(c => c.id === data.productCategory.id) ||
        null;

      const purchaseUnit =
        this.unitOptions.find(p => p.id === data.productPurchaseUnit.id) ||
        null;

      const salesUnit =
        this.unitOptions.find(s => s.id === data.productSalesUnit.id) || null;

      this.productItem.productId = data.productId;
      this.productItem.productCode = data.productCode || "";
      this.productItem.productCategory = category;
      this.productItem.productNameMain = data.productNameMain || "";
      this.productItem.productNameAdditional = data.productNameAdditional || "";
      this.productItem.productUnit = unit;
      this.productItem.productPurchaseUnit = purchaseUnit;
      this.productItem.productSalesUnit = salesUnit;
      this.productItem.productStatus = data.productStatus;
      if (
        data.productConversionUnitItems &&
        data.productConversionUnitItems.length > 0
      ) {
        this.showSubUnitTable = true;

        this.productItem.productConversionUnitItems =
          data.productConversionUnitItems.map(item => {
            const subUnit =
              this.unitOptions.find(u => u.id === item.uomId) || null;

            return {
              ...(item.id && { id: item.id }),
              productSubUnit: subUnit,
              productConversionUnit: item.qtyPerUnitOfMeasure || "",
            };
          });
      } else {
        this.productItem.productConversionUnitItems = [];
        this.showSubUnitTable = false;
      }
    },
    allowOnlyNumber(e) {
      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];
      if (allowedKeys.includes(e.key)) return;

      const input = e.target;
      const value = input.value || "";
      const { selectionStart, selectionEnd } = input;

      // Allow a single decimal point
      if (e.key === ".") {
        if (value.includes(".")) e.preventDefault();
        return;
      }

      // Digits 0–9: allow only if result would have at most 2 decimal places
      if (/^\d$/.test(e.key)) {
        const newValue =
          value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
        const parts = newValue.split(".");
        if (parts.length === 2 && parts[1].length > 2) {
          e.preventDefault();
        }
        return;
      }

      e.preventDefault();
    },
  },
  watch: {
    "productItem.productUnit"(newVal) {
      if (!this.isNewProduct) return;

      this.showSubUnitTable = false;

      if (!newVal) return;

      this.productItem.productPurchaseUnit = newVal;
      this.productItem.productSalesUnit = newVal;
    },
  },
};
</script>

<style scoped>
::v-deep .multiselect__tags {
  background-color: #ccecff6c;
  box-shadow: none;
}

::v-deep .multiselect__single,
::v-deep .multiselect__input,
::v-deep .multiselect__placeholder {
  background-color: transparent !important;
  word-break: break-word !important;
  white-space: normal !important;
}

::v-deep span.multiselect__placeholder {
  margin-left: 6px !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  padding-left: 6px;
  word-break: break-word !important;
  white-space: normal !important;
}

::v-deep span.multiselect__single {
  margin-left: 6px !important;
}

::v-deep .multiselect__single {
  margin-bottom: 0;
  padding-left: 0;
}

::v-deep .multiselect__tags {
  box-shadow: none;
  word-break: break-word !important;
  white-space: normal !important;
}

.sub-unit-table {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 16px 16px 0 16px;
  margin-bottom: 16px;
}

.layout-table {
  padding-left: 10px;
}

::v-deep #ProductSubUnitTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
  word-break: break-word !important;
  white-space: normal !important;
}

::v-deep td {
  padding: 8px;
}

::v-deep .bottom-add-row {
  /* margin-top: 10px !important; */
  text-align: left !important;
}

::v-deep #ProductSubUnitTable.table-hover tbody tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep .append-text {
  max-width: 100px;
  width: 100%;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
}

::v-deep .multiselect--disabled {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
  opacity: 1 !important;
}

::v-deep .multiselect--disabled .multiselect__tags {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  overflow: hidden;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__placeholder {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__select {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  border: 1px solid #ced4da !important;
  border-left: none !important;
  overflow: hidden;

  border-radius: 4px;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

::v-deep .multiselect__element .multiselect__option {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.btn-outline-primary.disabled,
.btn-outline-primary:disabled {
  color: #ebebeb;
  background-color: #fff;
  border-color: #ebebeb;
}

::v-deep #ProductSubUnitTable thead tr th:nth-child(1),
::v-deep #ProductSubUnitTable tbody tr:not(:last-child) td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep #ProductSubUnitTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep .form-control:disabled {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
}
</style>
